// SendX Clone — Complete Advanced Drips UI (V4.2 core + V4.3 polish, combined)
import React, { useEffect, useMemo, useState } from "react";
import "../dripsAdvancedV4.css";

const API = "/api";
async function api(path, opts = {}) {
  const r = await fetch(API + path, { headers: { "Content-Type": "application/json" }, ...opts, body: opts.body ? JSON.stringify(opts.body) : undefined });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
  return d;
}
const id6 = () => Math.random().toString(36).slice(2, 8);

const TRIGGER_OPTIONS = [
  { v: "manual", label: "Manual (API / bulk enroll)" },
  { v: "tag_added", label: "Tag added to contact" },
  { v: "list_joined", label: "Contact joins list" },
  { v: "form_submitted", label: "Contact submits form" },
  { v: "email_clicked", label: "Email link clicked" },
  { v: "api", label: "External API event" }
];

const STEP_TYPES = [
  { v: "email", label: "✉️ Send email" },
  { v: "wait", label: "⏱ Wait / delay" },
  { v: "condition", label: "➔ If / else branch" },
  { v: "ab_test", label: "A/B split test" },
  { v: "goal", label: "🎯 Goal check" },
  { v: "exit", label: "🛑 Exit sequence" },
  { v: "tag_add", label: "🏷️ Add tag" },
  { v: "tag_remove", label: "✖ Remove tag" }
];

export default function DripsAdvanced() {
  const [view, setView] = useState("list"); // list | editor | analytics
  const [sequences, setSequences] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");
  const [tab, setTab] = useState("sequences"); // sequences | bulk | timeline

  useEffect(() => { refresh(); }, []);
  async function refresh() {
    setLoading(true);
    try { const d = await api("/drips"); setSequences(d.sequences || []); }
    catch (e) { setToast("Load failed: " + e.message); }
    finally { setLoading(false); }
  }

  function notify(msg) { setToast(msg); setTimeout(() => setToast(""), 3500); }

  async function createFromTemplate(key) {
    try {
      const { templates } = await api("/drips/templates");
      const t = templates.find(x => x.key === key);
      if (!t) return;
      const seq = { name: t.name, status: "draft", trigger: { type: "manual" }, steps: t.steps.map(s => ({ ...s, id: id6() })) };
      const created = await api("/drips", { method: "POST", body: seq });
      notify("Created from template");
      setCurrent(created); setView("editor"); refresh();
    } catch (e) { notify(e.message); }
  }

  async function createBlank() {
    const seq = { name: "Untitled Sequence", status: "draft", trigger: { type: "manual" }, steps: [] };
    const created = await api("/drips", { method: "POST", body: seq });
    setCurrent(created); setView("editor"); refresh();
  }

  async function duplicate(id) {
    try { await api(`/drips/${id}/duplicate`, { method: "POST" }); notify("Duplicated"); refresh(); }
    catch (e) { notify(e.message); }
  }

  async function togglePause(seq) {
    try {
      await api(`/drips/${seq._id}/${seq.status === "active" ? "pause" : "resume"}`, { method: "POST" });
      refresh();
    } catch (e) { notify(e.message); }
  }

  async function del(id) {
    if (!confirm("Delete this sequence and all its runs?")) return;
    try { await api(`/drips/${id}`, { method: "DELETE" }); notify("Deleted"); refresh(); }
    catch (e) { notify(e.message); }
  }

  async function importFromFile(e) {
    const file = e.target.files?.[0]; if (!file) return;
    const text = await file.text();
    try {
      const data = JSON.parse(text);
      await api("/drips/import", { method: "POST", body: data });
      notify("Imported"); refresh();
    } catch (err) { notify("Import failed: " + err.message); }
    e.target.value = "";
  }

  return (
    <div className="drips-v4">
      <div className="dv4-topbar">
        <h2>Drip Campaigns <span className="dv4-badge">V4.3 FULL</span></h2>
        <div className="dv4-tabs">
          <button className={tab === "sequences" ? "active" : ""} onClick={() => setTab("sequences")}>Sequences</button>
          <button className={tab === "bulk" ? "active" : ""} onClick={() => setTab("bulk")}>Bulk Actions</button>
          <button className={tab === "timeline" ? "active" : ""} onClick={() => setTab("timeline")}>Contact Timeline</button>
        </div>
      </div>

      {toast && <div className="dv4-toast">{toast}</div>}

      {tab === "bulk" && <BulkActions sequences={sequences} notify={notify} />}
      {tab === "timeline" && <ContactTimeline notify={notify} />}

      {tab === "sequences" && view === "list" && (
        <>
          <div className="dv4-actions">
            <button className="dv4-btn primary" onClick={createBlank}>+ New Sequence</button>
            <div className="dv4-templates">
              <span>Or start from template:</span>
              <button onClick={() => createFromTemplate("welcome")}>Welcome</button>
              <button onClick={() => createFromTemplate("reengage")}>Re-engagement</button>
              <button onClick={() => createFromTemplate("abandoned_cart")}>Abandoned cart</button>
              <button onClick={() => createFromTemplate("onboarding")}>Onboarding</button>
              <button onClick={() => createFromTemplate("lead_nurture")}>Lead nurture</button>
            </div>
            <label className="dv4-import">
              Import JSON <input type="file" accept=".json" onChange={importFromFile} hidden />
            </label>
          </div>
          {loading && <p>Loading…</p>}
          <div className="dv4-list">
            {sequences.length === 0 && !loading && <p className="dv4-empty">No sequences yet. Create your first drip above.</p>}
            {sequences.map(s => (
              <div key={s._id} className="dv4-card">
                <div className="dv4-card-head">
                  <div>
                    <strong>{s.name}</strong>
                    <span className={`dv4-status dv4-${s.status}`}>{s.status}</span>
                  </div>
                  <div className="dv4-card-actions">
                    <button onClick={() => { setCurrent(s); setView("editor"); }}>Edit</button>
                    <button onClick={() => { setCurrent(s); setView("analytics"); }}>Analytics</button>
                    <button onClick={() => togglePause(s)}>{s.status === "active" ? "Pause" : "Activate"}</button>
                    <button onClick={() => duplicate(s._id)}>Duplicate</button>
                    <a className="dv4-linkbtn" href={`/api/drips/${s._id}/export`}>Export</a>
                    <button className="danger" onClick={() => del(s._id)}>Delete</button>
                  </div>
                </div>
                <div className="dv4-card-meta">
                  <span>{s.steps?.length || 0} steps</span>
                  <span>● Trigger: {s.trigger?.type || "manual"}</span>
                  <span>● Active: {s.stats?.active || 0}</span>
                  <span>● Completed: {s.stats?.completed || 0}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "sequences" && view === "editor" && current && (
        <SequenceEditor sequence={current} onBack={() => { setView("list"); refresh(); }} notify={notify} />
      )}

      {tab === "sequences" && view === "analytics" && current && (
        <SequenceAnalytics sequence={current} onBack={() => setView("list")} />
      )}
    </div>
  );
}

// ==================== Sequence Editor ====================
function SequenceEditor({ sequence, onBack, notify }) {
  const [seq, setSeq] = useState(sequence);
  const [dirty, setDirty] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  function update(patch) { setSeq(s => ({ ...s, ...patch })); setDirty(true); }
  function updateSettings(patch) { setSeq(s => ({ ...s, settings: { ...(s.settings || {}), ...patch } })); setDirty(true); }
  function updateSO(patch) { setSeq(s => ({ ...s, sendOptimization: { ...(s.sendOptimization || {}), ...patch } })); setDirty(true); }
  function updateFC(patch) { setSeq(s => ({ ...s, frequencyCap: { ...(s.frequencyCap || {}), ...patch } })); setDirty(true); }
  function updateSup(patch) { setSeq(s => ({ ...s, suppression: { ...(s.suppression || {}), ...patch } })); setDirty(true); }

  function updateStep(idx, patch) {
    setSeq(s => { const steps = [...s.steps]; steps[idx] = { ...steps[idx], ...patch }; return { ...s, steps }; });
    setDirty(true);
  }
  function addStep(type = "email") {
    const base = { id: id6(), type };
    if (type === "wait") Object.assign(base, { waitAmount: 1, waitUnit: "days" });
    if (type === "email") Object.assign(base, { subject: "", body: "" });
    if (type === "condition") Object.assign(base, { condition: { field: "tags", op: "has_tag", value: "" } });
    if (type === "ab_test") Object.assign(base, { variants: [{ id: "a", weight: 50, subject: "Variant A", body: "" }, { id: "b", weight: 50, subject: "Variant B", body: "" }], abWinner: { autoSelect: false, metric: "open", minSampleSize: 100, waitHours: 24 } });
    setSeq(s => ({ ...s, steps: [...(s.steps || []), base] }));
    setDirty(true);
  }
  function removeStep(idx) { setSeq(s => ({ ...s, steps: s.steps.filter((_, i) => i !== idx) })); setDirty(true); }
  function moveStep(idx, dir) {
    setSeq(s => {
      const steps = [...s.steps]; const j = idx + dir;
      if (j < 0 || j >= steps.length) return s;
      [steps[idx], steps[j]] = [steps[j], steps[idx]];
      return { ...s, steps };
    });
    setDirty(true);
  }

  // Drag & drop reorder
  const [dragIdx, setDragIdx] = useState(null);
  function onDragStart(i) { setDragIdx(i); }
  function onDragOver(e, i) { e.preventDefault(); }
  function onDrop(i) {
    if (dragIdx == null || dragIdx === i) return;
    setSeq(s => {
      const steps = [...s.steps];
      const [moved] = steps.splice(dragIdx, 1);
      steps.splice(i, 0, moved);
      return { ...s, steps };
    });
    setDragIdx(null); setDirty(true);
  }

  async function save() {
    try {
      const saved = await api(`/drips/${seq._id}`, { method: "PUT", body: seq });
      setSeq(saved); setDirty(false); notify("Saved");
    } catch (e) { notify("Save failed: " + e.message); }
  }

  async function testSend(stepId, variant) {
    if (!testEmail) return notify("Enter test email first");
    try {
      await api(`/drips/${seq._id}/test-send`, { method: "POST", body: { email: testEmail, stepId, variant } });
      notify("Test email sent to " + testEmail);
    } catch (e) { notify("Test failed: " + e.message); }
  }

  async function toggleStatus() {
    const target = seq.status === "active" ? "paused" : "active";
    update({ status: target });
    await api(`/drips/${seq._id}`, { method: "PUT", body: { ...seq, status: target } });
    notify(target === "active" ? "Activated" : "Paused");
  }

  return (
    <div className="dv4-editor">
      <div className="dv4-editor-head">
        <button className="dv4-back" onClick={onBack}>← Back</button>
        <input className="dv4-name" value={seq.name} onChange={e => update({ name: e.target.value })} />
        <span className={`dv4-status dv4-${seq.status}`}>{seq.status}</span>
        <div className="dv4-editor-actions">
          <input className="dv4-test-email" placeholder="test@you.com" value={testEmail} onChange={e => setTestEmail(e.target.value)} />
          <button onClick={toggleStatus}>{seq.status === "active" ? "Pause" : "Activate"}</button>
          <button className="dv4-btn primary" onClick={save} disabled={!dirty}>{dirty ? "Save changes" : "Saved"}</button>
        </div>
      </div>

      <div className="dv4-cols">
        <div className="dv4-col dv4-col-main">
          <section className="dv4-section">
            <h3>Trigger</h3>
            <div className="dv4-row">
              <label>Type
                <select value={seq.trigger?.type || "manual"} onChange={e => update({ trigger: { ...seq.trigger, type: e.target.value } })}>
                  {TRIGGER_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
              </label>
              {["tag_added", "list_joined", "form_submitted", "email_clicked"].includes(seq.trigger?.type) && (
                <label>Value
                  <input value={seq.trigger?.value || ""} onChange={e => update({ trigger: { ...seq.trigger, value: e.target.value } })}
                    placeholder={seq.trigger?.type === "email_clicked" ? "link substring, e.g. /pricing" : "tag/list/form name"} />
                </label>
              )}
            </div>
          </section>

          <section className="dv4-section">
            <div className="dv4-section-head">
              <h3>Steps ({seq.steps?.length || 0})</h3>
              <div className="dv4-add-step">
                {STEP_TYPES.map(t => <button key={t.v} onClick={() => addStep(t.v)}>{t.label}</button>)}
              </div>
            </div>
            <div className="dv4-steps">
              {(seq.steps || []).map((step, idx) => (
                <div key={step.id} className="dv4-step" draggable
                  onDragStart={() => onDragStart(idx)} onDragOver={e => onDragOver(e, idx)} onDrop={() => onDrop(idx)}>
                  <div className="dv4-step-head">
                    <span className="dv4-drag-handle" title="Drag to reorder">☰</span>
                    <span className="dv4-step-num">{idx + 1}</span>
                    <select value={step.type} onChange={e => updateStep(idx, { type: e.target.value })}>
                      {STEP_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                    </select>
                    <div className="dv4-step-actions">
                      <button onClick={() => moveStep(idx, -1)}>↑</button>
                      <button onClick={() => moveStep(idx, +1)}>↓</button>
                      {step.type === "email" && <button onClick={() => testSend(step.id)}>Test</button>}
                      <button className="danger" onClick={() => removeStep(idx)}>✖</button>
                    </div>
                  </div>
                  <StepEditor step={step} onChange={p => updateStep(idx, p)} testSend={testSend} steps={seq.steps} />
                </div>
              ))}
              {(!seq.steps || seq.steps.length === 0) && <p className="dv4-empty">No steps yet. Add your first step above.</p>}
            </div>
          </section>
        </div>

        <div className="dv4-col dv4-col-side">
          <section className="dv4-section">
            <h3>Send-time optimization</h3>
            <label>Strategy
              <select value={seq.sendOptimization?.strategy || "immediate"} onChange={e => updateSO({ strategy: e.target.value })}>
                <option value="immediate">Send immediately</option>
                <option value="business_hours">Only during business hours (server UTC)</option>
                <option value="contact_timezone">Contact's local timezone</option>
              </select>
            </label>
            {seq.sendOptimization?.strategy === "contact_timezone" && (
              <label>Default timezone
                <input value={seq.sendOptimization?.defaultTimezone || "UTC"} onChange={e => updateSO({ defaultTimezone: e.target.value })} placeholder="e.g. Asia/Karachi" />
              </label>
            )}
            {["business_hours", "contact_timezone"].includes(seq.sendOptimization?.strategy) && (
              <div className="dv4-row">
                <label>Start hour<input type="number" min={0} max={23} value={seq.settings?.businessHoursStart ?? 9} onChange={e => updateSettings({ businessHoursStart: +e.target.value })} /></label>
                <label>End hour<input type="number" min={0} max={23} value={seq.settings?.businessHoursEnd ?? 18} onChange={e => updateSettings({ businessHoursEnd: +e.target.value })} /></label>
              </div>
            )}
            <label className="dv4-check">
              <input type="checkbox" checked={seq.settings?.sendOnWeekends ?? true} onChange={e => updateSettings({ sendOnWeekends: e.target.checked })} />
              Send on weekends
            </label>
          </section>

          <section className="dv4-section">
            <h3>Frequency cap</h3>
            <label className="dv4-check">
              <input type="checkbox" checked={seq.frequencyCap?.enabled || false} onChange={e => updateFC({ enabled: e.target.checked })} />
              Limit emails per contact per 24h
            </label>
            {seq.frequencyCap?.enabled && (
              <label>Max per 24h<input type="number" min={1} value={seq.frequencyCap?.maxPer24h || 3} onChange={e => updateFC({ maxPer24h: +e.target.value })} /></label>
            )}
          </section>

          <section className="dv4-section">
            <h3>Suppression</h3>
            <label>Skip contacts with these tags (comma-separated)
              <input value={(seq.suppression?.excludeTags || []).join(", ")}
                onChange={e => updateSup({ excludeTags: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                placeholder="unsubscribed, vip" />
            </label>
          </section>

          <section className="dv4-section">
            <h3>Goal</h3>
            <label className="dv4-check">
              <input type="checkbox" checked={seq.goal?.enabled || false} onChange={e => update({ goal: { ...seq.goal, enabled: e.target.checked } })} />
              Enable exit goal
            </label>
            {seq.goal?.enabled && (
              <div className="dv4-row">
                <label>Type
                  <select value={seq.goal?.type || "tag"} onChange={e => update({ goal: { ...seq.goal, type: e.target.value } })}>
                    <option value="tag">Contact gets tag</option>
                    <option value="url_visit">Contact visits URL</option>
                    <option value="unsubscribe">Contact unsubscribes</option>
                  </select>
                </label>
                <label>Value
                  <input value={seq.goal?.value || ""} onChange={e => update({ goal: { ...seq.goal, value: e.target.value } })} />
                </label>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

// ==================== Step Editor ====================
function StepEditor({ step, onChange, testSend, steps }) {
  if (step.type === "email") return <EmailStepEditor step={step} onChange={onChange} testSend={() => testSend(step.id)} />;
  if (step.type === "wait") return (
    <div className="dv4-row">
      <label>Wait<input type="number" min={1} value={step.waitAmount || 1} onChange={e => onChange({ waitAmount: +e.target.value })} /></label>
      <label>Unit
        <select value={step.waitUnit || "hours"} onChange={e => onChange({ waitUnit: e.target.value })}>
          <option value="minutes">Minutes</option><option value="hours">Hours</option><option value="days">Days</option>
        </select>
      </label>
    </div>
  );
  if (step.type === "condition") return (
    <div className="dv4-row">
      <label>Field<input value={step.condition?.field || ""} onChange={e => onChange({ condition: { ...step.condition, field: e.target.value } })} placeholder="tags, email, firstName" /></label>
      <label>Op
        <select value={step.condition?.op || "has_tag"} onChange={e => onChange({ condition: { ...step.condition, op: e.target.value } })}>
          <option value="has_tag">has tag</option><option value="not_has_tag">doesn't have tag</option>
          <option value="equals">equals</option><option value="not_equals">not equals</option>
          <option value="contains">contains</option><option value="exists">exists</option>
        </select>
      </label>
      <label>Value<input value={step.condition?.value || ""} onChange={e => onChange({ condition: { ...step.condition, value: e.target.value } })} /></label>
      <label>If YES → step
        <select value={step.yesStepId || ""} onChange={e => onChange({ yesStepId: e.target.value })}>
          <option value="">(next)</option>
          {steps.filter(s => s.id !== step.id).map(s => <option key={s.id} value={s.id}>{s.type} — {s.subject || s.id}</option>)}
        </select>
      </label>
      <label>If NO → step
        <select value={step.noStepId || ""} onChange={e => onChange({ noStepId: e.target.value })}>
          <option value="">(next)</option>
          {steps.filter(s => s.id !== step.id).map(s => <option key={s.id} value={s.id}>{s.type} — {s.subject || s.id}</option>)}
        </select>
      </label>
    </div>
  );
  if (step.type === "ab_test") return <AbTestEditor step={step} onChange={onChange} testSend={v => testSend(step.id, v)} />;
  if (step.type === "tag_add" || step.type === "tag_remove") return (
    <label>Tag name<input value={step.tag || ""} onChange={e => onChange({ tag: e.target.value })} placeholder="e.g. onboarded" /></label>
  );
  if (step.type === "goal") return <p className="dv4-hint">Goal check evaluates the sequence goal. Configured in sidebar.</p>;
  if (step.type === "exit") return <p className="dv4-hint">Contact exits the sequence when this step is reached.</p>;
  return null;
}

function EmailStepEditor({ step, onChange, testSend }) {
  const [mode, setMode] = useState(step.blocks?.length ? "blocks" : "html");
  return (
    <div className="dv4-email-editor">
      <div className="dv4-row">
        <label style={{flex:2}}>Subject<input value={step.subject || ""} onChange={e => onChange({ subject: e.target.value })} placeholder="Hey {{first_name}}, quick question" /></label>
        <label>From name<input value={step.fromName || ""} onChange={e => onChange({ fromName: e.target.value })} placeholder="(default)" /></label>
        <label>From email<input value={step.fromEmail || ""} onChange={e => onChange({ fromEmail: e.target.value })} placeholder="(default)" /></label>
      </div>
      <div className="dv4-mode-toggle">
        <button className={mode === "html" ? "active" : ""} onClick={() => setMode("html")}>HTML</button>
        <button className={mode === "blocks" ? "active" : ""} onClick={() => setMode("blocks")}>Blocks</button>
        <button className="dv4-mini-test" onClick={testSend}>Send test</button>
      </div>
      {mode === "html" ? (
        <textarea className="dv4-body" value={step.body || ""} onChange={e => onChange({ body: e.target.value })} placeholder="<p>Hi {{first_name}},</p>" rows={6} />
      ) : (
        <BlockEditor blocks={step.blocks || []} onChange={blocks => onChange({ blocks })} />
      )}
      <p className="dv4-hint">Merge tags: <code>{"{{first_name}}"}</code> <code>{"{{last_name}}"}</code> <code>{"{{email}}"}</code></p>
    </div>
  );
}

function BlockEditor({ blocks, onChange }) {
  const [dragIdx, setDragIdx] = useState(null);
  function add(type) {
    const b = { type };
    if (type === "heading") Object.assign(b, { level: 2, text: "Heading" });
    if (type === "paragraph") Object.assign(b, { text: "Your paragraph text" });
    if (type === "button") Object.assign(b, { text: "Click here", url: "https://", color: "#0b74de" });
    if (type === "image") Object.assign(b, { url: "", alt: "" });
    if (type === "html") Object.assign(b, { html: "<div>Custom HTML</div>" });
    onChange([...blocks, b]);
  }
  function upd(i, patch) { const c = [...blocks]; c[i] = { ...c[i], ...patch }; onChange(c); }
  function rm(i) { onChange(blocks.filter((_, k) => k !== i)); }
  function onDrop(i) {
    if (dragIdx == null || dragIdx === i) return;
    const c = [...blocks]; const [m] = c.splice(dragIdx, 1); c.splice(i, 0, m); onChange(c); setDragIdx(null);
  }
  return (
    <div className="dv4-blocks">
      <div className="dv4-block-toolbar">
        {["heading","paragraph","button","image","divider","spacer","html"].map(t => <button key={t} onClick={() => add(t)}>+ {t}</button>)}
      </div>
      {blocks.map((b, i) => (
        <div key={i} className="dv4-block" draggable onDragStart={() => setDragIdx(i)} onDragOver={e => e.preventDefault()} onDrop={() => onDrop(i)}>
          <div className="dv4-block-head"><span>☰ {b.type}</span><button className="danger" onClick={() => rm(i)}>✖</button></div>
          {b.type === "heading" && <><input value={b.text || ""} onChange={e => upd(i, { text: e.target.value })} /><input type="number" min={1} max={6} value={b.level || 2} onChange={e => upd(i, { level: +e.target.value })} style={{width:60}} /></>}
          {b.type === "paragraph" && <textarea value={b.text || ""} onChange={e => upd(i, { text: e.target.value })} rows={3} />}
          {b.type === "button" && <><input value={b.text || ""} onChange={e => upd(i, { text: e.target.value })} placeholder="Button text" /><input value={b.url || ""} onChange={e => upd(i, { url: e.target.value })} placeholder="URL" /><input value={b.color || ""} onChange={e => upd(i, { color: e.target.value })} placeholder="#0b74de" /></>}
          {b.type === "image" && <><input value={b.url || ""} onChange={e => upd(i, { url: e.target.value })} placeholder="Image URL" /><input value={b.alt || ""} onChange={e => upd(i, { alt: e.target.value })} placeholder="Alt text" /></>}
          {b.type === "html" && <textarea value={b.html || ""} onChange={e => upd(i, { html: e.target.value })} rows={3} />}
          {b.type === "spacer" && <input type="number" value={b.height || 20} onChange={e => upd(i, { height: +e.target.value })} />}
        </div>
      ))}
    </div>
  );
}

function AbTestEditor({ step, onChange, testSend }) {
  const variants = step.variants || [];
  function updV(i, patch) { const c = [...variants]; c[i] = { ...c[i], ...patch }; onChange({ variants: c }); }
  function addV() { onChange({ variants: [...variants, { id: String.fromCharCode(97 + variants.length), weight: 50, subject: "", body: "" }] }); }
  function rmV(i) { onChange({ variants: variants.filter((_, k) => k !== i) }); }
  return (
    <div className="dv4-abtest">
      {variants.map((v, i) => (
        <div key={i} className="dv4-variant">
          <div className="dv4-row">
            <label>ID<input value={v.id || ""} onChange={e => updV(i, { id: e.target.value })} style={{width:60}} /></label>
            <label>Weight %<input type="number" min={1} value={v.weight || 50} onChange={e => updV(i, { weight: +e.target.value })} /></label>
            <label style={{flex:2}}>Subject<input value={v.subject || ""} onChange={e => updV(i, { subject: e.target.value })} /></label>
            <button onClick={() => testSend(v.id)}>Test</button>
            <button className="danger" onClick={() => rmV(i)}>✖</button>
          </div>
          <textarea value={v.body || ""} onChange={e => updV(i, { body: e.target.value })} placeholder="HTML body" rows={4} />
        </div>
      ))}
      <button onClick={addV}>+ Add variant</button>
      <div className="dv4-row dv4-winner">
        <label className="dv4-check">
          <input type="checkbox" checked={step.abWinner?.autoSelect || false} onChange={e => onChange({ abWinner: { ...step.abWinner, autoSelect: e.target.checked } })} />
          Auto-select winner
        </label>
        {step.abWinner?.autoSelect && <>
          <label>Metric<select value={step.abWinner?.metric || "open"} onChange={e => onChange({ abWinner: { ...step.abWinner, metric: e.target.value } })}><option value="open">Open rate</option><option value="click">Click rate</option></select></label>
          <label>Min sample<input type="number" value={step.abWinner?.minSampleSize || 100} onChange={e => onChange({ abWinner: { ...step.abWinner, minSampleSize: +e.target.value } })} /></label>
          <label>Wait hours<input type="number" value={step.abWinner?.waitHours || 24} onChange={e => onChange({ abWinner: { ...step.abWinner, waitHours: +e.target.value } })} /></label>
        </>}
      </div>
    </div>
  );
}

// ==================== Analytics ====================
function SequenceAnalytics({ sequence, onBack }) {
  const [data, setData] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [heatStepId, setHeatStepId] = useState("");
  useEffect(() => { load(); }, []);
  useEffect(() => { loadHeatmap(); }, [heatStepId]);
  async function load() {
    try { setData(await api(`/drips/${sequence._id}/analytics`)); } catch {}
  }
  async function loadHeatmap() {
    try {
      const q = heatStepId ? `?stepId=${heatStepId}` : "";
      setHeatmap(await api(`/drips/${sequence._id}/heatmap${q}`));
    } catch {}
  }
  const maxH = heatmap ? Math.max(1, ...heatmap.hours) : 1;
  return (
    <div className="dv4-analytics">
      <div className="dv4-editor-head"><button className="dv4-back" onClick={onBack}>← Back</button><h3>{sequence.name} — Analytics</h3></div>
      {data && (
        <>
          <div className="dv4-stats">
            <div><strong>{data.total}</strong> total enrollments</div>
            <div><strong>{data.active}</strong> active</div>
            <div><strong>{data.completed}</strong> completed</div>
            <div><strong>{data.exited}</strong> exited</div>
            <div><strong>{data.removed}</strong> removed</div>
          </div>
          <table className="dv4-perstep">
            <thead><tr><th>Step</th><th>Sent</th><th>Opens</th><th>Clicks</th><th>Open %</th><th>Click %</th></tr></thead>
            <tbody>
              {sequence.steps.filter(s => s.type === "email" || s.type === "ab_test").map(s => {
                const d = data.perStep[s.id] || { sent: 0, opens: 0, clicks: 0 };
                return <tr key={s.id}><td>{s.subject || s.id}</td><td>{d.sent}</td><td>{d.opens}</td><td>{d.clicks}</td><td>{d.sent ? Math.round(d.opens / d.sent * 100) : 0}%</td><td>{d.sent ? Math.round(d.clicks / d.sent * 100) : 0}%</td></tr>;
              })}
            </tbody>
          </table>
          <div className="dv4-section">
            <h4>Delivery hour heatmap (UTC)</h4>
            <label>Step: <select value={heatStepId} onChange={e => setHeatStepId(e.target.value)}>
              <option value="">All steps</option>
              {sequence.steps.filter(s => s.type === "email" || s.type === "ab_test").map(s => <option key={s.id} value={s.id}>{s.subject || s.id}</option>)}
            </select></label>
            {heatmap && (
              <div className="dv4-heatmap">
                {heatmap.hours.map((n, i) => (
                  <div key={i} className="dv4-hcell" style={{ background: `rgba(11,116,222,${n / maxH * 0.9 + 0.05})` }} title={`${i}:00 UTC — ${n} sent`}>
                    <span>{i}</span><small>{n}</small>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ==================== Bulk Actions (V4.3) ====================
function BulkActions({ sequences, notify }) {
  const [seqId, setSeqId] = useState("");
  const [source, setSource] = useState("tag");
  const [value, setValue] = useState("");
  const [mode, setMode] = useState("enroll");
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function run() {
    if (!seqId) return notify("Choose a sequence");
    if (!value.trim()) return notify("Enter a value");
    setBusy(true); setResult(null);
    try {
      const endpoint = mode === "enroll" ? `/drips/${seqId}/bulk-enroll` : `/drips/${seqId}/bulk-remove`;
      const body = { source, value: source === "segment" && value.trim().startsWith("{") ? JSON.parse(value) : value.trim() };
      if (mode === "enroll") body.dryRun = dryRun;
      const d = await api(endpoint, { method: "POST", body });
      setResult(d);
      notify(mode === "enroll" ? (dryRun ? `${d.matched} matched` : `Enrolled ${d.enrolled}`) : `Removed ${d.removed}`);
    } catch (e) { notify(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="dv4-panel">
      <h3>Bulk Actions</h3>
      <p className="dv4-hint">Enroll or remove many contacts at once, filtered by list, tag, or segment.</p>
      <div className="dv4-row">
        <label>Sequence<select value={seqId} onChange={e => setSeqId(e.target.value)}><option value="">— Choose —</option>{sequences.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}</select></label>
        <label>Action<select value={mode} onChange={e => setMode(e.target.value)}><option value="enroll">Enroll</option><option value="remove">Remove</option></select></label>
        <label>Source<select value={source} onChange={e => setSource(e.target.value)}><option value="tag">Tag</option><option value="list">List</option><option value="segment">Segment (JSON)</option></select></label>
        <label style={{flex:2}}>Value<input value={value} onChange={e => setValue(e.target.value)} placeholder={source === "segment" ? '{"tags":"vip"}' : "e.g. newsletter"} /></label>
      </div>
      {mode === "enroll" && <label className="dv4-check"><input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} /> Dry run (count only)</label>}
      <button className="dv4-btn primary" onClick={run} disabled={busy}>{busy ? "Running…" : mode === "enroll" ? (dryRun ? "Preview" : "Enroll now") : "Remove now"}</button>
      {result && <pre className="dv4-result">{JSON.stringify(result, null, 2)}</pre>}
    </div>
  );
}

// ==================== Contact Timeline (V4.3) ====================
function ContactTimeline({ notify }) {
  const [email, setEmail] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  async function load() {
    if (!email.trim()) return notify("Enter contact email");
    setBusy(true);
    try { setData(await api(`/contacts/by-email/${encodeURIComponent(email.trim())}/drip-timeline`)); }
    catch (e) { notify(e.message); setData(null); }
    finally { setBusy(false); }
  }
  return (
    <div className="dv4-panel">
      <h3>Contact Drip Timeline</h3>
      <p className="dv4-hint">See every drip a contact has been enrolled in, with full event history.</p>
      <div className="dv4-row">
        <input style={{flex:1}} value={email} onChange={e => setEmail(e.target.value)} placeholder="user@example.com" onKeyDown={e => e.key === "Enter" && load()} />
        <button className="dv4-btn primary" onClick={load} disabled={busy}>{busy ? "…" : "Load"}</button>
      </div>
      {data && (
        <div className="dv4-timeline">
          <div className="dv4-tl-head"><strong>{data.email}</strong><span>{data.timeline.length} sequence(s)</span></div>
          {data.timeline.length === 0 && <p className="dv4-empty">No drip activity.</p>}
          {data.timeline.map(run => (
            <details key={run.runId} className="dv4-run">
              <summary><span>{run.sequenceName}</span><span className={`dv4-status dv4-${run.status}`}>{run.status}</span><span className="dv4-when">{new Date(run.enrolledAt).toLocaleString()}</span></summary>
              <ul className="dv4-events">
                {run.events.map((ev, i) => (
                  <li key={i}><span className="dv4-ev-name">{ev.event}</span><span className="dv4-ev-time">{new Date(ev.at).toLocaleString()}</span>{ev.meta && Object.keys(ev.meta).length > 0 && <code>{JSON.stringify(ev.meta)}</code>}</li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}
